import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { 
  Users,
  Activity,
  Clock,
  CheckSquare,
  MessageSquare,
  LogIn,
  Calendar,
  Download,
  RefreshCw,
  TrendingUp,
  Eye,
  ArrowLeft,
  ListTodo,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Circle,
  Filter,
  UserCircle,
  Trash2,
  RotateCcw,
  XCircle
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamActivityPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [teamData, setTeamData] = useState(null);
  const [selectedDays, setSelectedDays] = useState('7');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [activeTab, setActiveTab] = useState('activity');
  
  // Task Overview state
  const [taskOverviewLoading, setTaskOverviewLoading] = useState(false);
  const [teamTasks, setTeamTasks] = useState([]);
  const [taskUsers, setTaskUsers] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedTaskUser, setSelectedTaskUser] = useState('all');
  const [expandedUsers, setExpandedUsers] = useState({});

  // Deleted Tasks state
  const [deletedTasksLoading, setDeletedTasksLoading] = useState(false);
  const [deletedTasks, setDeletedTasks] = useState([]);
  const [selectedDeletedTasks, setSelectedDeletedTasks] = useState([]);

  const fetchTeamActivity = useCallback(async () => {
    try {
      const response = await fetch(`${API}/activity/team?days=${selectedDays}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeamData(data);
      } else if (response.status === 403) {
        toast.error('Access denied. Super admin privileges required.');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching team activity:', error);
      toast.error('Failed to load team activity');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, selectedDays, navigate]);

  const fetchTeamTasks = useCallback(async () => {
    setTaskOverviewLoading(true);
    try {
      const monthParam = selectedMonth === 'all' ? 'all' : selectedMonth;
      const response = await fetch(`${API}/admin/team-tasks?month=${monthParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeamTasks(data.tasks || []);
        setTaskUsers(data.users || []);
        
        // Default all sections to collapsed
        setExpandedUsers({});
      }
    } catch (error) {
      console.error('Error fetching team tasks:', error);
    } finally {
      setTaskOverviewLoading(false);
    }
  }, [token, selectedMonth]);

  const fetchDeletedTasks = useCallback(async () => {
    setDeletedTasksLoading(true);
    try {
      const response = await fetch(`${API}/admin/deleted-tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeletedTasks(data.tasks || []);
      }
    } catch (error) {
      console.error('Error fetching deleted tasks:', error);
    } finally {
      setDeletedTasksLoading(false);
    }
  }, [token]);

  const handleRestoreTasks = async () => {
    if (selectedDeletedTasks.length === 0) return;
    try {
      const response = await fetch(`${API}/admin/restore-tasks`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_ids: selectedDeletedTasks })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Restored ${data.success.length} task(s)`);
        setSelectedDeletedTasks([]);
        fetchDeletedTasks();
      }
    } catch (error) {
      toast.error('Failed to restore tasks');
    }
  };

  const handlePurgeTasks = async () => {
    if (selectedDeletedTasks.length === 0) return;
    try {
      const response = await fetch(`${API}/admin/purge-tasks`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_ids: selectedDeletedTasks })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Permanently deleted ${data.success.length} task(s)`);
        setSelectedDeletedTasks([]);
        fetchDeletedTasks();
      }
    } catch (error) {
      toast.error('Failed to purge tasks');
    }
  };

  const toggleDeletedTaskSelection = (taskId) => {
    setSelectedDeletedTasks(prev => 
      prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
    );
  };

  const toggleAllDeletedTasks = () => {
    if (selectedDeletedTasks.length === deletedTasks.length) {
      setSelectedDeletedTasks([]);
    } else {
      setSelectedDeletedTasks(deletedTasks.map(t => t.id));
    }
  };

  useEffect(() => {
    fetchTeamActivity();
  }, [fetchTeamActivity]);

  useEffect(() => {
    if (activeTab === 'tasks') {
      fetchTeamTasks();
    } else if (activeTab === 'deleted') {
      fetchDeletedTasks();
    }
  }, [activeTab, fetchTeamTasks, fetchDeletedTasks]);

  const handleRefresh = () => {
    setRefreshing(true);
    if (activeTab === 'activity') {
      fetchTeamActivity();
    } else if (activeTab === 'tasks') {
      fetchTeamTasks();
    } else if (activeTab === 'deleted') {
      fetchDeletedTasks();
      setRefreshing(false);
    }
  };

  const exportToCSV = () => {
    if (!teamData) return;
    
    const headers = ['User Name', 'Email', 'Total Sessions', 'Total Duration (min)', 'Avg Session (min)', 'Longest Session (min)', 'Total Logins', 'Tasks Created', 'Tasks Completed', 'Messages Sent', 'Last Active'];
    const rows = (teamData.users || teamData.user_stats || []).map(user => [
      user.user_name || user.name,
      user.user_email || user.email,
      user.total_sessions,
      user.total_duration_minutes,
      user.average_session_minutes || user.avg_session_minutes,
      user.longest_session_minutes,
      user.logins,
      user.tasks_created,
      user.tasks_completed,
      user.messages_sent,
      user.last_active || 'Never'
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-activity-${selectedDays}days.csv`;
    a.click();
    toast.success('Activity report exported');
  };

  const exportTasksToCSV = () => {
    const headers = ['User', 'Task Name', 'Status', 'Priority', 'Due Date', 'Created At'];
    const rows = teamTasks.map(task => [
      task.assignee_name,
      task.name,
      task.status,
      task.ai_priority || 'medium',
      task.due_date || 'No due date',
      task.created_at
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-tasks-${selectedMonth}.csv`;
    a.click();
    toast.success('Task report exported');
  };

  const getActivityLevel = (user) => {
    const score = (user.logins * 10) + (user.tasks_created * 20) + (user.messages_sent * 5) + (user.total_duration_minutes);
    if (score >= 500) return { level: 'High', color: 'bg-green-500/20 text-green-400 border-green-500/30' };
    if (score >= 100) return { level: 'Medium', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
    return { level: 'Inactive', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30' };
  };

  const toggleUserExpanded = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'in_progress':
      case 'in progress':
        return <Clock className="w-4 h-4 text-blue-500" />;
      default:
        return <Circle className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'in progress': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      pending: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
      new: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    };
    return styles[status] || styles.pending;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-red-500/20 text-red-400',
      medium: 'bg-yellow-500/20 text-yellow-400',
      low: 'bg-green-500/20 text-green-400'
    };
    return styles[priority] || styles.medium;
  };

  const isInProgress = (status) => status === 'in_progress' || status === 'in progress';

  // Group tasks by user
  const tasksByUser = taskUsers.map(u => ({
    ...u,
    tasks: teamTasks.filter(t => t.assigned_to === u.id),
    stats: {
      total: teamTasks.filter(t => t.assigned_to === u.id).length,
      completed: teamTasks.filter(t => t.assigned_to === u.id && t.status === 'completed').length,
      inProgress: teamTasks.filter(t => t.assigned_to === u.id && isInProgress(t.status)).length,
      overdue: teamTasks.filter(t => t.assigned_to === u.id && t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed').length
    }
  })).filter(u => selectedTaskUser === 'all' || u.id === selectedTaskUser);

  // Generate month options (last 12 months)
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    return {
      value: format(date, 'yyyy-MM'),
      label: format(date, 'MMMM yyyy')
    };
  });

  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold" data-testid="page-title">Team Management</h1>
                <p className="text-sm text-muted-foreground">Monitor activity and manage team tasks</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <TabsList className="grid w-full sm:w-auto grid-cols-3">
                <TabsTrigger value="activity" className="flex items-center gap-2" data-testid="activity-tab">
                  <Activity className="w-4 h-4" />
                  <span className="hidden sm:inline">Team Activity</span>
                  <span className="sm:hidden">Activity</span>
                </TabsTrigger>
                <TabsTrigger value="tasks" className="flex items-center gap-2" data-testid="tasks-tab">
                  <ListTodo className="w-4 h-4" />
                  <span className="hidden sm:inline">Task Overview</span>
                  <span className="sm:hidden">Tasks</span>
                </TabsTrigger>
                <TabsTrigger value="deleted" className="flex items-center gap-2" data-testid="deleted-tab">
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Deleted Tasks</span>
                  <span className="sm:hidden">Deleted</span>
                  {deletedTasks.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1 text-xs">
                      {deletedTasks.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Controls based on active tab */}
              <div className="flex items-center gap-2">
                {activeTab === 'activity' ? (
                  <>
                    <Select value={selectedDays} onValueChange={setSelectedDays}>
                      <SelectTrigger className="w-[140px]" data-testid="days-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Last 24 hours</SelectItem>
                        <SelectItem value="7">Last 7 days</SelectItem>
                        <SelectItem value="14">Last 14 days</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                        <SelectItem value="90">Last 90 days</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn">
                      <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button variant="outline" onClick={exportToCSV} data-testid="export-btn">
                      <Download className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Export</span>
                    </Button>
                  </>
                ) : activeTab === 'tasks' ? (
                  <>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger className="w-[160px]" data-testid="month-filter">
                        <Calendar className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        {monthOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={selectedTaskUser} onValueChange={setSelectedTaskUser}>
                      <SelectTrigger className="w-[160px]" data-testid="user-filter">
                        <UserCircle className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="All Users" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        {taskUsers.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={handleRefresh} disabled={taskOverviewLoading} data-testid="refresh-tasks-btn">
                      <RefreshCw className={`w-4 h-4 ${taskOverviewLoading ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button variant="outline" onClick={exportTasksToCSV} data-testid="export-tasks-btn">
                      <Download className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Export</span>
                    </Button>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={handleRefresh} disabled={deletedTasksLoading} data-testid="refresh-deleted-btn">
                      <RefreshCw className={`w-4 h-4 ${deletedTasksLoading ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const resp = await fetch(`${API}/admin/cleanup-notifications`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          if (resp.ok) {
                            const data = await resp.json();
                            toast.success(data.message);
                          }
                        } catch (e) {
                          toast.error('Failed to clean up notifications');
                        }
                      }}
                      data-testid="cleanup-notifications-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Clean Up Notifications</span>
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Activity Tab Content */}
            <TabsContent value="activity" className="space-y-4">
              {teamData && (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Users className="w-4 h-4" />
                          <span className="text-xs">Active Users</span>
                        </div>
                        <p className="text-2xl font-bold">{teamData.active_users || teamData.summary?.active_users || 0}</p>
                        <p className="text-xs text-muted-foreground">of {teamData.total_users || teamData.summary?.total_users || 0} total</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Clock className="w-4 h-4" />
                          <span className="text-xs">Total Time</span>
                        </div>
                        <p className="text-2xl font-bold">{Math.round(((teamData.users || teamData.user_stats || []).reduce((sum, u) => sum + (u.total_duration_minutes || 0), 0)) / 60)}h</p>
                        <p className="text-xs text-muted-foreground">{(teamData.users || teamData.user_stats || []).reduce((sum, u) => sum + (u.total_duration_minutes || 0), 0)} minutes</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <LogIn className="w-4 h-4" />
                          <span className="text-xs">Total Logins</span>
                        </div>
                        <p className="text-2xl font-bold">{(teamData.users || teamData.user_stats || []).reduce((sum, u) => sum + (u.logins || 0), 0)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <CheckSquare className="w-4 h-4" />
                          <span className="text-xs">Tasks Created</span>
                        </div>
                        <p className="text-2xl font-bold">{(teamData.users || teamData.user_stats || []).reduce((sum, u) => sum + (u.tasks_created || 0), 0)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <MessageSquare className="w-4 h-4" />
                          <span className="text-xs">Messages</span>
                        </div>
                        <p className="text-2xl font-bold">{(teamData.users || teamData.user_stats || []).reduce((sum, u) => sum + (u.messages_sent || 0), 0)}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* User Activity Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">User Activity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">User</th>
                              <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground hidden sm:table-cell">Activity</th>
                              <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Sessions</th>
                              <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground hidden md:table-cell">Time Online</th>
                              <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Tasks</th>
                              <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground hidden lg:table-cell">Messages</th>
                              <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground hidden xl:table-cell">Last Active</th>
                              <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(teamData.users || teamData.user_stats || []).map((userData) => {
                              const activity = getActivityLevel(userData);
                              return (
                                <tr key={userData.user_id || userData.id} className="border-b hover:bg-muted/50">
                                  <td className="py-3 px-2">
                                    <div className="flex items-center gap-2">
                                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium">
                                        {(userData.user_name || userData.name)?.charAt(0) || '?'}
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{userData.user_name || userData.name}</p>
                                        <p className="text-xs text-muted-foreground hidden sm:block">{userData.user_email || userData.email}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="py-3 px-2 hidden sm:table-cell">
                                    <Badge variant="outline" className={activity.color}>
                                      {activity.level}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-2 text-center text-sm">{userData.total_sessions}</td>
                                  <td className="py-3 px-2 text-center text-sm hidden md:table-cell">
                                    {Math.round(userData.total_duration_minutes / 60)}h {userData.total_duration_minutes % 60}m
                                  </td>
                                  <td className="py-3 px-2 text-center text-sm">
                                    <span className="text-green-600">{userData.tasks_created}</span>
                                    {userData.tasks_completed > 0 && (
                                      <span className="text-muted-foreground text-xs ml-1">
                                        ({userData.tasks_completed} done)
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-3 px-2 text-center text-sm hidden lg:table-cell">{userData.messages_sent}</td>
                                  <td className="py-3 px-2 text-sm hidden xl:table-cell">
                                    {userData.last_active 
                                      ? formatDistanceToNow(new Date(userData.last_active), { addSuffix: true })
                                      : 'Never'
                                    }
                                  </td>
                                  <td className="py-3 px-2 text-center">
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => {
                                        setSelectedUser(userData);
                                        setShowUserModal(true);
                                      }}
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* Task Overview Tab Content */}
            <TabsContent value="tasks" className="space-y-4">
              {taskOverviewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <ListTodo className="w-4 h-4" />
                          <span className="text-xs">Total Tasks</span>
                        </div>
                        <p className="text-2xl font-bold">{teamTasks.length}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-xs">Completed</span>
                        </div>
                        <p className="text-2xl font-bold text-green-600">
                          {teamTasks.filter(t => t.status === 'completed').length}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Clock className="w-4 h-4 text-blue-500" />
                          <span className="text-xs">In Progress</span>
                        </div>
                        <p className="text-2xl font-bold text-blue-600">
                          {teamTasks.filter(t => isInProgress(t.status)).length}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <AlertCircle className="w-4 h-4 text-red-500" />
                          <span className="text-xs">Overdue</span>
                        </div>
                        <p className="text-2xl font-bold text-red-600">
                          {teamTasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed').length}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Tasks by User */}
                  <div className="space-y-3">
                    {tasksByUser.length === 0 ? (
                      <Card>
                        <CardContent className="p-8 text-center text-muted-foreground">
                          <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>No tasks found for the selected period</p>
                        </CardContent>
                      </Card>
                    ) : (
                      tasksByUser.map(userGroup => (
                        <Card key={userGroup.id}>
                          <Collapsible open={expandedUsers[userGroup.id]} onOpenChange={() => toggleUserExpanded(userGroup.id)}>
                            <CollapsibleTrigger className="w-full">
                              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-lg font-medium">
                                      {userGroup.name?.charAt(0) || '?'}
                                    </div>
                                    <div className="text-left">
                                      <CardTitle className="text-base">{userGroup.name}</CardTitle>
                                      <p className="text-xs text-muted-foreground">{userGroup.email}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2 text-sm">
                                      <Badge variant="outline">{userGroup.stats.total} tasks</Badge>
                                      {userGroup.stats.completed > 0 && (
                                        <Badge className="bg-green-500/20 text-green-600 border-green-500/30">
                                          {userGroup.stats.completed} done
                                        </Badge>
                                      )}
                                      {userGroup.stats.overdue > 0 && (
                                        <Badge className="bg-red-500/20 text-red-600 border-red-500/30">
                                          {userGroup.stats.overdue} overdue
                                        </Badge>
                                      )}
                                    </div>
                                    {expandedUsers[userGroup.id] ? (
                                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                                    )}
                                  </div>
                                </div>
                              </CardHeader>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <CardContent className="pt-0">
                                {/* Progress Bar */}
                                <div className="mb-4">
                                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                    <span>Completion Rate</span>
                                    <span>
                                      {userGroup.stats.total > 0 
                                        ? Math.round((userGroup.stats.completed / userGroup.stats.total) * 100) 
                                        : 0}%
                                    </span>
                                  </div>
                                  <Progress 
                                    value={userGroup.stats.total > 0 
                                      ? (userGroup.stats.completed / userGroup.stats.total) * 100 
                                      : 0} 
                                    className="h-2"
                                  />
                                </div>
                                
                                {/* Task List */}
                                <div className="space-y-2">
                                  {userGroup.tasks.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-4">No tasks assigned</p>
                                  ) : (
                                    userGroup.tasks.map(task => (
                                      <div 
                                        key={task.id} 
                                        className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                                        onClick={() => navigate(`/tasks/${task.id}`)}
                                      >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                          {getStatusIcon(task.status)}
                                          <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{task.name}</p>
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                              {task.due_date && (
                                                <span className={new Date(task.due_date) < new Date() && task.status !== 'completed' ? 'text-red-500' : ''}>
                                                  Due: {format(new Date(task.due_date), 'MMM d')}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Badge className={getPriorityBadge(task.ai_priority)} variant="outline">
                                            {task.ai_priority || 'medium'}
                                          </Badge>
                                          <Badge className={getStatusBadge(task.status)} variant="outline">
                                            {task.status?.replace('_', ' ')}
                                          </Badge>
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </CardContent>
                            </CollapsibleContent>
                          </Collapsible>
                        </Card>
                      ))
                    )}
                  </div>
                </>
              )}
            </TabsContent>

            {/* Deleted Tasks Tab Content */}
            <TabsContent value="deleted" className="space-y-4">
              {deletedTasksLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {/* Bulk Action Bar */}
                  {selectedDeletedTasks.length > 0 && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted border" data-testid="deleted-bulk-actions">
                      <span className="text-sm font-medium">{selectedDeletedTasks.length} selected</span>
                      <Button size="sm" variant="outline" onClick={handleRestoreTasks} data-testid="restore-btn">
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Restore
                      </Button>
                      <Button size="sm" variant="destructive" onClick={handlePurgeTasks} data-testid="purge-btn">
                        <XCircle className="w-4 h-4 mr-2" />
                        Permanently Delete
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedDeletedTasks([])} data-testid="clear-selection-btn">
                        Clear
                      </Button>
                    </div>
                  )}

                  {deletedTasks.length === 0 ? (
                    <Card>
                      <CardContent className="p-8 text-center text-muted-foreground">
                        <Trash2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No deleted tasks</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b">
                                <th className="py-3 px-3 text-left">
                                  <input
                                    type="checkbox"
                                    checked={selectedDeletedTasks.length === deletedTasks.length && deletedTasks.length > 0}
                                    onChange={toggleAllDeletedTasks}
                                    className="rounded"
                                    data-testid="select-all-deleted"
                                  />
                                </th>
                                <th className="py-3 px-2 text-left text-sm font-medium text-muted-foreground">Task Name</th>
                                <th className="py-3 px-2 text-left text-sm font-medium text-muted-foreground hidden sm:table-cell">Assignee</th>
                                <th className="py-3 px-2 text-left text-sm font-medium text-muted-foreground hidden md:table-cell">Status</th>
                                <th className="py-3 px-2 text-left text-sm font-medium text-muted-foreground hidden lg:table-cell">Deleted By</th>
                                <th className="py-3 px-2 text-left text-sm font-medium text-muted-foreground">Deleted</th>
                                <th className="py-3 px-2 text-center text-sm font-medium text-muted-foreground">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deletedTasks.map(task => (
                                <tr key={task.id} className="border-b hover:bg-muted/50">
                                  <td className="py-3 px-3">
                                    <input
                                      type="checkbox"
                                      checked={selectedDeletedTasks.includes(task.id)}
                                      onChange={() => toggleDeletedTaskSelection(task.id)}
                                      className="rounded"
                                      data-testid={`select-deleted-${task.id}`}
                                    />
                                  </td>
                                  <td className="py-3 px-2">
                                    <button
                                      className="text-left hover:underline cursor-pointer"
                                      onClick={() => navigate(`/tasks/${task.id}`)}
                                      data-testid={`deleted-task-${task.id}`}
                                    >
                                      <p className="font-medium text-sm truncate max-w-[300px]">{task.name}</p>
                                    </button>
                                  </td>
                                  <td className="py-3 px-2 text-sm hidden sm:table-cell">{task.assignee_name || task.assigned_to_name}</td>
                                  <td className="py-3 px-2 hidden md:table-cell">
                                    <Badge className={getStatusBadge(task.status)} variant="outline">
                                      {task.status?.replace('_', ' ')}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-2 text-sm hidden lg:table-cell">{task.deleted_by_name || 'Unknown'}</td>
                                  <td className="py-3 px-2 text-sm text-muted-foreground">
                                    {task.deleted_at
                                      ? formatDistanceToNow(new Date(task.deleted_at), { addSuffix: true })
                                      : 'Unknown'}
                                  </td>
                                  <td className="py-3 px-2 text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => navigate(`/tasks/${task.id}`)}
                                      data-testid={`view-deleted-${task.id}`}
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* User Detail Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>User Activity Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-xl font-medium">
                  {(selectedUser.user_name || selectedUser.name)?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="font-medium">{selectedUser.user_name || selectedUser.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedUser.user_email || selectedUser.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-xs text-muted-foreground">Total Sessions</p>
                  <p className="text-lg font-bold">{selectedUser.total_sessions}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-xs text-muted-foreground">Time Online</p>
                  <p className="text-lg font-bold">{Math.round(selectedUser.total_duration_minutes / 60)}h {selectedUser.total_duration_minutes % 60}m</p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-xs text-muted-foreground">Avg Session</p>
                  <p className="text-lg font-bold">{selectedUser.average_session_minutes || selectedUser.avg_session_minutes || 0}m</p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-xs text-muted-foreground">Longest Session</p>
                  <p className="text-lg font-bold">{selectedUser.longest_session_minutes || 0}m</p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-xs text-muted-foreground">Tasks Created</p>
                  <p className="text-lg font-bold">{selectedUser.tasks_created}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-xs text-muted-foreground">Messages Sent</p>
                  <p className="text-lg font-bold">{selectedUser.messages_sent}</p>
                </div>
              </div>
              
              {selectedUser.last_active && (
                <p className="text-sm text-muted-foreground">
                  Last active: {formatDistanceToNow(new Date(selectedUser.last_active), { addSuffix: true })}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamActivityPage;
